import React, { useMemo, useState } from 'react';
import { Mail, MapPin, Clock, Instagram, Facebook } from 'lucide-react';
import { COMPANY_INFO, IMAGES, COLLECTIONS } from '../mock';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', vehicle: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Autocomplete options: all vehicle collections + a generic fallback
  const productOptions = useMemo(() => {
    const fromCollections = COLLECTIONS.map((c) => c.name).sort();
    return ['General inquiry / Multiple products', 'Other / Not listed', ...fromCollections];
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.vehicle || !form.message) {
      toast({ title: 'Missing fields', description: 'Name, email, product/vehicle and message are required.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Could not send your message.');
      }
      // Keep a local backup for the user too
      const messages = JSON.parse(localStorage.getItem('fad_messages') || '[]');
      messages.push({ ...form, at: new Date().toISOString() });
      localStorage.setItem('fad_messages', JSON.stringify(messages));
      toast({
        title: 'Message sent',
        description: 'Thanks — our team will reply within 1 business day.',
      });
      setForm({ name: '', email: '', vehicle: '', message: '' });
    } catch (err) {
      toast({
        title: 'Send failed',
        description: err.message || 'Please email info@fortuneautodesign.com directly.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-black text-white">
      <section
        className="relative pt-40 pb-20"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.92), rgba(0,0,0,0.7)), url(${IMAGES.darkGarage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">CONTACT</div>
          <h1 className="font-display text-5xl md:text-7xl tracking-tight max-w-3xl">GET IN TOUCH.</h1>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_1.3fr] gap-12">
          <div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-6">GET IN TOUCH</h2>
            <div className="h-[2px] w-16 bg-[#FF6B00] mb-8" />
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <Mail className="text-[#FF6B00] mt-1" size={22} />
                <div>
                  <div className="font-display tracking-[0.2em] text-xs text-neutral-500 mb-1">EMAIL</div>
                  <div className="text-neutral-200">{COMPANY_INFO.email}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-[#FF6B00] mt-1" size={22} />
                <div>
                  <div className="font-display tracking-[0.2em] text-xs text-neutral-500 mb-1">ADDRESS</div>
                  <div className="text-neutral-200">{COMPANY_INFO.address}</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="text-[#FF6B00] mt-1" size={22} />
                <div>
                  <div className="font-display tracking-[0.2em] text-xs text-neutral-500 mb-1">HOURS</div>
                  <div className="text-neutral-200">{COMPANY_INFO.hours}</div>
                </div>
              </div>
            </div>
            <div className="mt-10 flex items-center gap-5">
              <a href={COMPANY_INFO.socials.instagram} target="_blank" rel="noreferrer" className="text-neutral-300 hover:text-[#FF6B00]"><Instagram size={22} /></a>
              <a href={COMPANY_INFO.socials.facebook} target="_blank" rel="noreferrer" className="text-neutral-300 hover:text-[#FF6B00]"><Facebook size={22} /></a>
            </div>
          </div>

          <form onSubmit={submit} className="bg-neutral-950 border border-white/10 p-8 md:p-10">
            <h3 className="font-display text-3xl md:text-4xl tracking-wide mb-2">DO YOU HAVE A QUESTION?</h3>
            <p className="text-neutral-400 text-sm mb-6 leading-relaxed">
              Questions about orders or products?<br />
              Send us a message and we&rsquo;ll get back to you as soon as possible.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="font-display tracking-[0.2em] text-[11px] text-neutral-400">NAME *</label>
                <input value={form.name} onChange={update('name')} className="mt-2 w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00]" placeholder="John Doe" />
              </div>
              <div>
                <label className="font-display tracking-[0.2em] text-[11px] text-neutral-400">EMAIL *</label>
                <input value={form.email} onChange={update('email')} type="email" className="mt-2 w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00]" placeholder="you@example.com" />
              </div>
              <div>
                <label className="font-display tracking-[0.2em] text-[11px] text-neutral-400">PRODUCT OR VEHICLE *</label>
                <input
                  list="contact-product-options"
                  value={form.vehicle}
                  onChange={update('vehicle')}
                  data-testid="contact-product-input"
                  required
                  className="mt-2 w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00]"
                  placeholder="Start typing your vehicle or product..."
                />
                <datalist id="contact-product-options">
                  {productOptions.map((opt) => (
                    <option key={opt} value={opt} />
                  ))}
                </datalist>
              </div>
            </div>
            <div className="mt-5">
              <label className="font-display tracking-[0.2em] text-[11px] text-neutral-400">MESSAGE *</label>
              <textarea value={form.message} onChange={update('message')} rows={6} className="mt-2 w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF6B00]" placeholder="How can we help?" />
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-6 bg-[#FF6B00] hover:bg-[#E25500] transition-colors px-8 py-4 font-display tracking-[0.22em] text-sm font-bold disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? 'SENDING\u2026' : 'SEND MESSAGE'}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
